import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { buildSeo } from "@/lib/seo";
import { STATES } from "@/data/states";
import { STATE_GAMES, MULTISTATE_GAMES } from "@/data/games";

export const Route = createFileRoute("/routes-index")({
  head: () =>
    buildSeo({
      title: "All Game Routes — USA Lottery Club",
      description:
        "Complete index of every state and multi-state game page on USA Lottery Club. Every clickable URL on the site, in one place.",
      path: "/routes-index",
    }),
  component: RoutesIndex,
});

function stripStatePrefix(stateSlug: string, gameSlug: string): string {
  return gameSlug.startsWith(`${stateSlug}-`)
    ? gameSlug.slice(stateSlug.length + 1)
    : gameSlug;
}

function RoutesIndex() {
  const multistate = MULTISTATE_GAMES.map((g) => ({
    href: `/games/${g.slug}`,
    label: g.name,
  }));

  const stateBuckets = STATES.filter((s) => s.has_lottery).map((state) => {
    const games = STATE_GAMES.filter((g) => g.state_id === state.id).map((g) => {
      const slug = stripStatePrefix(state.slug, g.slug);
      return { href: `/${state.slug}/${slug}`, label: g.name };
    });
    return { state, games };
  });

  return (
    <Layout>
      <SEOHead
        title="All Game Routes — USA Lottery Club"
        description="Complete index of every state and multi-state game page."
      />
      <div className="container py-8">
        <h1 className="font-sans text-[30px] font-bold leading-tight mb-3 tracking-tight">
          Route Index
        </h1>
        <p className="font-sans text-[16px] leading-6 text-muted-foreground mb-8 max-w-[640px]">
          Every canonical game URL on the site. Use this page to verify
          coverage or jump to any state or multi-state game.
        </p>

        <section className="mb-10">
          <h2 className="font-sans text-[20px] font-bold leading-7 mb-4 tracking-tight">
            Multi-state games
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {multistate.map((m) => (
              <li key={m.href}>
                <Link
                  to="/games/$gameSlug"
                  params={{ gameSlug: m.href.split("/").pop()! }}
                  className="block rounded-lg border bg-card px-3 py-2 text-[14px] hover:shadow-sm"
                >
                  <span className="font-medium">{m.label}</span>
                  <span className="block text-[12px] text-muted-foreground">
                    {m.href}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-sans text-[20px] font-bold leading-7 mb-4 tracking-tight">
            State games
          </h2>
          <div className="space-y-6">
            {stateBuckets
              .filter((b) => b.games.length > 0)
              .map(({ state, games }) => (
                <div key={state.id}>
                  <h3 className="font-sans text-[16px] font-semibold mb-2">
                    <Link
                      to="/states/$stateSlug"
                      params={{ stateSlug: state.slug }}
                      className="hover:underline"
                    >
                      {state.name}
                    </Link>
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {games.map((g) => {
                      const gameSlug = g.href.split("/").pop()!;
                      return (
                        <li key={g.href}>
                          <Link
                            to="/$stateSlug/$gameSlug"
                            params={{ stateSlug: state.slug, gameSlug }}
                            className="block rounded-lg border bg-card px-3 py-2 text-[14px] hover:shadow-sm"
                          >
                            <span className="font-medium">{g.label}</span>
                            <span className="block text-[12px] text-muted-foreground">
                              {g.href}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}